import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../config/axiosConfig';
import './Gallery.css';
import {
  FaFilter, FaSearch, FaRegImage, FaChevronDown, FaExclamationTriangle,
  FaChevronLeft, FaChevronRight, FaTimes, FaTh, FaList,
  FaEye, FaCalendarAlt, FaTag, FaBuilding, FaDownload
} from 'react-icons/fa';
import { useTranslation } from '../contexts/TranslationContext';

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [databaseDepartments, setDatabaseDepartments] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayTimerRef = useRef(null);

  // Modal state
  const [selectedImage, setSelectedImage] = useState(null);

  // Download state (track which image is downloading)
  const [downloadingId, setDownloadingId] = useState(null);

  const { t, language } = useTranslation();

  // Refs for scroll animation sections
  const heroRef = useRef(null);
  const filtersRef = useRef(null);
  const gridHeaderRef = useRef(null);
  const statsRef = useRef(null);

  // Department color mapping
  const getDepartmentColor = (departmentName) => {
    if (!departmentName) return '#3498db';
    const colorMap = {
      'tailor': '#E91E63', 'sewing': '#E91E63', 'fashion': '#E91E63',
      'farm': '#4CAF50', 'agriculture': '#4CAF50', 'organic': '#4CAF50',
      'culinary': '#FF9800', 'cooking': '#FF9800', 'food': '#FF9800',
      'cosmetology': '#9C27B0', 'beauty': '#9C27B0', 'soap': '#9C27B0',
      'literacy': '#2196F3', 'education': '#2196F3',
      'handicraft': '#795548', 'art': '#795548', 'craft': '#795548',
      'leather': '#6F4E37', 'shoe': '#6F4E37', 'bag': '#6F4E37',
      'milk': '#6F4E37', 'yogurt': '#6F4E37', 'dairy': '#6F4E37',
      'carpet': '#FF5722', 'coffee': '#6F4E37', 'housekeeping': '#4CAF50',
      'reception': '#2196F3', 'weaving': '#8B4513'
    };
    const nameLower = departmentName.toLowerCase();
    for (const [key, color] of Object.entries(colorMap)) {
      if (nameLower.includes(key.toLowerCase())) return color;
    }
    return '#3498db';
  };

  const getArrayData = (responseData) => {
    if (!responseData) return [];
    if (Array.isArray(responseData)) return responseData;
    const possibleProperties = ['data', 'rows', 'result', 'results', 'items', 'images', 'departments', 'gallery'];
    for (const prop of possibleProperties) {
      if (responseData[prop] && Array.isArray(responseData[prop])) return responseData[prop];
    }
    if (typeof responseData === 'object' && responseData !== null && !Array.isArray(responseData)) return [responseData];
    return [];
  };

  const getSelectedDepartment = () => {
    if (!Array.isArray(databaseDepartments) || databaseDepartments.length === 0) {
      return { id: 'all', name: t('viewAll') || 'All Departments', color: '#FF9800' };
    }
    const dept = databaseDepartments.find(d => d && d.id === selectedDepartment);
    if (dept) return { ...dept, color: dept.color || '#FF9800' };
    return databaseDepartments[0] || { id: 'all', name: t('viewAll') || 'All Departments', color: '#FF9800' };
  };

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get('https://hsn.urugowoc.org.rw/backend/api/departments', {
          params: { lang: language, active: true, limit: 50 }
        });
        const departmentsArray = getArrayData(response.data);
        let deptOptions = [];
        if (departmentsArray.length > 0) {
          deptOptions = departmentsArray.map(dept => ({
            id: dept.id ? dept.id.toString() : Math.random().toString(),
            name: dept.name || dept.department_name || dept.title || t('unknownDepartment') || 'Unknown Department',
            color: dept.color || getDepartmentColor(dept.name || dept.department_name) || '#3498db',
            description: dept.description || ''
          }));
        }
        const allDepartments = [
          { id: 'all', name: t('viewAll') || 'All Departments', color: '#FF9800', description: 'View images from all departments' },
          ...deptOptions
        ];
        setDatabaseDepartments(allDepartments);
      } catch (err) {
        console.error('Error fetching departments:', err);
        const fallbackDepartments = [
          { id: 'all', name: t('viewAll') || 'All Departments', color: '#FF9800', description: 'View images from all departments' },
          { id: '1', name: t('tailoringFashion') || 'Tailoring & Fashion', color: '#E91E63', description: t('tailoringDesc') || 'Professional sewing and fashion design' },
          { id: '2', name: t('organicFarming') || 'Organic Farming', color: '#4CAF50', description: t('farmingDesc') || 'Sustainable agriculture practices' },
          { id: '3', name: t('culinaryArts') || 'Culinary Arts', color: '#FF9800', description: t('culinaryDesc') || 'Professional cooking skills' }
        ];
        setDatabaseDepartments(fallbackDepartments);
        setError(t('errorLoadingDepartments') || 'Error loading departments. Using default list.');
      }
    };
    fetchDepartments();
  }, [language, t]);

  // Fetch images — ONLY from database, no fallback filling
  useEffect(() => {
    const fetchImages = async () => {
      try {
        setError(null);

        const params = { lang: language };
        if (selectedDepartment !== 'all') params.department_id = selectedDepartment;

        const response = await axios.get(
          'https://hsn.urugowoc.org.rw/backend/api/gallery-images',
          { params }
        );

        const imagesArray = getArrayData(response.data);

        if (imagesArray.length > 0) {
          const processedImages = imagesArray
            .map((img, index) => ({
              id: img.id || `gallery-img-${index}`,
              title: img.title || img.caption || `Gallery Image ${index + 1}`,
              description: img.description || img.caption || '',
              image_url: img.image_url || img.image_path || img.url || img.image || '',
              department_name: img.department_name || img.department || '',
              department_id: img.department_id || img.departmentId || '',
              category: img.category || img.type || '',
              created_at: img.created_at || img.uploaded_at || new Date().toISOString(),
              featured: img.is_featured === 1 || img.featured === true,
              order: img.sort_order || img.order || index
            }))
            .filter(img =>
              img.image_url &&
              typeof img.image_url === 'string' &&
              img.image_url.trim().length > 0
            );

          console.log(`✅ Loaded ${processedImages.length} gallery images from DB`);
          setImages(processedImages);
          setFilteredImages(processedImages);
          setCurrentSlide(0);
        } else {
          console.warn('⚠️ No active gallery images in database');
          setImages([]);
          setFilteredImages([]);
        }
      } catch (err) {
        console.error('❌ Error fetching gallery images:', err);
        setImages([]);
        setFilteredImages([]);
        setError(t('errorLoadingImages') || `Error loading images: ${err.message}`);
      }
    };

    if (databaseDepartments.length > 0) fetchImages();
  }, [language, selectedDepartment, databaseDepartments.length, t]);

  // Filter images based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredImages(images);
      return;
    }
    const searchLower = searchTerm.toLowerCase();
    const filtered = images.filter(img =>
      (img.title && img.title.toLowerCase().includes(searchLower)) ||
      (img.description && img.description.toLowerCase().includes(searchLower)) ||
      (img.department_name && img.department_name.toLowerCase().includes(searchLower)) ||
      (img.category && img.category.toLowerCase().includes(searchLower))
    );
    setFilteredImages(filtered);
  }, [searchTerm, images]);

  // ===== SLIDER FUNCTIONS =====
  const totalSlides = images.length;

  const nextSlide = useCallback(() => {
    if (totalSlides > 0) setCurrentSlide((prev) => (prev + 1) % totalSlides);
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    if (totalSlides > 0) setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  }, [totalSlides]);

  const goToSlide = useCallback((index) => setCurrentSlide(index), []);

  const resetAutoPlayTimer = useCallback(() => {
    if (autoPlayTimerRef.current) clearInterval(autoPlayTimerRef.current);
    if (isAutoPlaying && totalSlides > 1) {
      autoPlayTimerRef.current = setInterval(nextSlide, 5000);
    }
  }, [isAutoPlaying, totalSlides, nextSlide]);

  useEffect(() => {
    resetAutoPlayTimer();
    return () => {
      if (autoPlayTimerRef.current) clearInterval(autoPlayTimerRef.current);
    };
  }, [resetAutoPlayTimer]);

  const startAutoPlay = useCallback(() => setIsAutoPlaying(true), []);

  const stopAutoPlay = useCallback(() => {
    setIsAutoPlaying(false);
    if (autoPlayTimerRef.current) clearInterval(autoPlayTimerRef.current);
  }, []);

  // ===== HANDLERS =====
  const handleDepartmentSelect = (deptId) => {
    setSelectedDepartment(deptId);
    setShowDropdown(false);
    setSearchTerm('');
  };

  const getDepartmentName = (deptId) => {
    if (deptId === 'all') return t('viewAll') || 'All Departments';
    const dept = databaseDepartments.find(d => d && d.id === deptId);
    return dept ? dept.name : t('viewAll') || 'All Departments';
  };

  // Format image URL
  const formatImageUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    const baseUrl = 'https://hsn.urugowoc.org.rw';
    if (cleanUrl.includes('backend/')) return `${baseUrl}/${cleanUrl}`;
    return `${baseUrl}/backend/${cleanUrl}`;
  };

  // ============================================================
  // ✅ NEW: DOWNLOAD FUNCTION
  // Downloads the image using fetch + blob (works across all browsers
  // even when the "download" attribute is ignored for cross-origin URLs)
  // ============================================================
  const sanitizeFilename = (name, fallback = 'gallery-image') => {
    if (!name || typeof name !== 'string') return fallback;
    return name
      .trim()
      .replace(/[\\/:*?"<>|]+/g, '-')   // strip illegal filename characters
      .replace(/\s+/g, '_')              // replace whitespace with underscore
      .substring(0, 80);                 // limit length
  };

  const handleDownload = async (e, image) => {
    // Prevent modal from opening when clicking download inside the grid
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!image) return;

    const url = formatImageUrl(image.image_url);
    if (!url) {
      setError(t('cannotDownloadImage') || 'Cannot download this image — invalid URL.');
      return;
    }

    // Get file extension from URL
    let ext = 'jpg';
    try {
      const urlPath = new URL(url).pathname;
      const match = urlPath.match(/\.([a-zA-Z0-9]+)$/);
      if (match) ext = match[1].toLowerCase();
    } catch {
      // ignore
    }

    const baseName = sanitizeFilename(
      image.title || image.description || `gallery_${image.id}`,
      `gallery_${image.id || Date.now()}`
    );
    const filename = `${baseName}.${ext}`;

    setDownloadingId(image.id);

    try {
      // Use fetch so cross-origin images download as blobs instead of opening
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 2000);
    } catch (err) {
      console.error('❌ Download failed, trying fallback:', err);
      // Fallback: open in new tab so the user can save manually
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (fallbackErr) {
        console.error('❌ Fallback download failed:', fallbackErr);
        setError(t('downloadFailed') || 'Failed to download image. Please try again.');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const openModal = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  // Intersection Observer for scroll animations
  useEffect(() => {
    const sections = [heroRef.current, filtersRef.current, gridHeaderRef.current, statsRef.current].filter(ref => ref !== null);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    sections.forEach(section => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="gallery-wrapper">
      {/* Hero Slider Section */}
      <section ref={heroRef} className="gallery-hero-section gallery-fade-up">
        <div
          className="gallery-hero-slider"
          onMouseEnter={stopAutoPlay}
          onMouseLeave={startAutoPlay}
        >
          <div
            className="gallery-hero-slides"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {images.length > 0 ? (
              images.map((image, idx) => {
                const heroImageUrl = formatImageUrl(image.image_url);
                return (
                  <div key={image.id || idx} className="gallery-hero-slide">
                    {heroImageUrl && (
                      <div
                        className="gallery-hero-slide-bg"
                        style={{ backgroundImage: `url(${heroImageUrl})` }}
                      />
                    )}
                    <div className="gallery-hero-slide-overlay">
                      <div className="gallery-hero-slide-content">
                        <h2>{image.title}</h2>
                        <p>{image.description}</p>
                        <span className="gallery-hero-slide-dept">
                          {image.department_name || 'Urugo WOC'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="gallery-hero-slide gallery-hero-slide-empty">
                <div className="gallery-hero-slide-overlay">
                  <div className="gallery-hero-slide-content">
                    <h2>{t('welcomeToGallery') || 'Welcome to Our Gallery'}</h2>
                    <p>{t('gallerySubtitle') || 'Explore the journey of women empowerment'}</p>
                    <span className="gallery-hero-slide-dept">Urugo WOC</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {images.length > 1 && (
            <>
              <button className="gallery-hero-nav gallery-hero-prev" onClick={prevSlide}>
                <FaChevronLeft />
              </button>
              <button className="gallery-hero-nav gallery-hero-next" onClick={nextSlide}>
                <FaChevronRight />
              </button>
              <div className="gallery-hero-dots">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    className={`gallery-hero-dot ${idx === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(idx)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Error Banner */}
      {error && (
        <div className="gallery-error-banner">
          <FaExclamationTriangle className="gallery-error-icon" />
          <div className="gallery-error-content">
            <p className="gallery-error-message">{error}</p>
            <button className="gallery-retry-button" onClick={() => window.location.reload()}>
              {t('tryAgain') || 'Try Again'}
            </button>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <section ref={filtersRef} className="gallery-filters-section gallery-fade-up">
        <div className="gallery-filters-container">
          <div className="gallery-search-filter">
            <div className="gallery-search-input-wrapper">
              <FaSearch className="gallery-search-icon" />
              <input
                type="text"
                placeholder={t('searchGallery') || 'Search gallery...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="gallery-search-input"
              />
            </div>
          </div>
          <div className="gallery-department-filters">
            <div className="gallery-filter-header">
              <FaFilter />
              <h3>{t('filterBy') || 'Filter by Department'}</h3>
            </div>
            <div className="gallery-custom-dropdown">
              {(() => {
                const selectedDept = getSelectedDepartment();
                return (
                  <div
                    className="gallery-dropdown-selected"
                    onClick={() => setShowDropdown(!showDropdown)}
                    style={{ borderColor: selectedDept.color, backgroundColor: showDropdown ? '#f9f9f9' : 'white' }}
                  >
                    <div className="gallery-selected-content">
                      <span className="gallery-dept-color-indicator" style={{ backgroundColor: selectedDept.color }} />
                      <span className="gallery-selected-text">{selectedDept.name}</span>
                      {selectedDept.description && <span className="gallery-selected-description">{selectedDept.description}</span>}
                    </div>
                    <FaChevronDown className={`gallery-dropdown-arrow ${showDropdown ? 'gallery-rotate' : ''}`} />
                  </div>
                );
              })()}
              {showDropdown && (
                <div className="gallery-dropdown-menu">
                  {Array.isArray(databaseDepartments) && databaseDepartments.length > 0 ? (
                    databaseDepartments.map(dept => (
                      <div
                        key={dept.id}
                        className={`gallery-dropdown-item ${selectedDepartment === dept.id ? 'gallery-selected' : ''}`}
                        onClick={() => handleDepartmentSelect(dept.id)}
                      >
                        <span className="gallery-dept-color-indicator" style={{ backgroundColor: dept.color || '#3498db' }} />
                        <div className="gallery-dropdown-item-content">
                          <span className="gallery-dropdown-text">{dept.name}</span>
                          {dept.description && <span className="gallery-dropdown-description">{dept.description}</span>}
                        </div>
                        {selectedDepartment === dept.id && <span className="gallery-checkmark">✓</span>}
                      </div>
                    ))
                  ) : (
                    <div className="gallery-dropdown-empty">
                      <p>{t('noDepartmentsAvailable') || 'No departments available'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Grid Section */}
      <section className="gallery-grid-section">
        <div ref={gridHeaderRef} className="gallery-grid-header gallery-fade-up">
          <div className="gallery-grid-header-left">
            <h2>{getDepartmentName(selectedDepartment)}</h2>
            <p className="gallery-image-count">
              {filteredImages.length} {t('images') || 'Images'}
              {searchTerm && ` ${t('matching') || 'matching'} "${searchTerm}"`}
            </p>
          </div>
          <div className="gallery-grid-header-right">
            <button
              className={`gallery-view-toggle ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              aria-label="Grid view"
            >
              <FaTh />
            </button>
            <button
              className={`gallery-view-toggle ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              aria-label="List view"
            >
              <FaList />
            </button>
          </div>
        </div>

        {filteredImages.length === 0 ? (
          <div className="gallery-no-images">
            <FaRegImage className="gallery-no-images-icon" />
            <h3>{t('noImagesFound') || 'No images found'}</h3>
            <p>{searchTerm ? t('tryDifferentSearch') || 'Try a different search term' : t('noImagesInDepartment') || 'No images available for this department yet'}</p>
            <button className="gallery-reset-button" onClick={() => { setSearchTerm(''); setSelectedDepartment('all'); }}>
              {t('resetFilters') || 'Reset Filters'}
            </button>
          </div>
        ) : (
          <div className={`gallery-grid gallery-view-${viewMode}`}>
            {filteredImages.map((image, index) => {
              const deptColor = getDepartmentColor(image.department_name);
              const imageUrl = formatImageUrl(image.image_url);
              const isDownloading = downloadingId === image.id;

              return (
                <div
                  key={image.id || `gallery-item-${index}`}
                  className="gallery-grid-item"
                  onClick={() => openModal(image)}
                >
                  <div className="gallery-image-container">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={image.title || `Gallery image ${index + 1}`}
                        loading="lazy"
                        className="gallery-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.parentElement) {
                            e.target.parentElement.classList.add('gallery-image-error');
                          }
                        }}
                      />
                    ) : (
                      <div className="gallery-image-error-placeholder">
                        <FaRegImage />
                        <span>{t('imageUnavailable') || 'Image unavailable'}</span>
                      </div>
                    )}

                    {/* ✅ ALWAYS-VISIBLE DOWNLOAD BUTTON (bottom-left) */}
                    <button
                      type="button"
                      className={`gallery-download-btn gallery-download-btn--persistent ${isDownloading ? 'gallery-download-btn--loading' : ''}`}
                      onClick={(e) => handleDownload(e, image)}
                      title={t('downloadImage') || 'Download image'}
                      aria-label={t('downloadImage') || 'Download image'}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <span className="gallery-download-spinner" />
                      ) : (
                        <FaDownload />
                      )}
                      <span className="gallery-download-btn-label">
                        {isDownloading ? (t('downloading') || 'Downloading…') : (t('download') || 'Download')}
                      </span>
                    </button>

                    {/* Hover overlay */}
                    <div className="gallery-image-overlay">
                      <div className="gallery-overlay-top">
                        <div className="gallery-dept-badge" style={{ backgroundColor: deptColor }}>
                          {image.department_name || 'Department'}
                        </div>
                      </div>
                      <div className="gallery-overlay-bottom">
                        <div className="gallery-click-hint">
                          <FaEye /> {t('clickToView') || 'Click to view'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ===== IMAGE MODAL ===== */}
      {selectedImage && (
        <div className="gallery-modal-overlay" onClick={closeModal}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-modal-close" onClick={closeModal}>
              <FaTimes />
            </button>
            <div className="gallery-modal-image-wrapper">
              {(() => {
                const modalImageUrl = formatImageUrl(selectedImage.image_url);
                if (!modalImageUrl) {
                  return (
                    <div className="gallery-modal-image-error">
                      <FaRegImage />
                      <p>{t('imageUnavailable') || 'Image unavailable'}</p>
                    </div>
                  );
                }
                return (
                  <img
                    src={modalImageUrl}
                    alt={selectedImage.title}
                    className="gallery-modal-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.parentElement) {
                        e.target.parentElement.classList.add('gallery-modal-image-error-wrapper');
                      }
                    }}
                  />
                );
              })()}
            </div>
            <div className="gallery-modal-caption">
              <h3>{selectedImage.title}</h3>
              <p className="gallery-modal-description">{selectedImage.description}</p>
              <div className="gallery-modal-meta">
                {selectedImage.department_name && (
                  <span className="gallery-modal-dept" style={{ backgroundColor: getDepartmentColor(selectedImage.department_name) }}>
                    <FaBuilding /> {selectedImage.department_name}
                  </span>
                )}
                {selectedImage.category && (
                  <span className="gallery-modal-category">
                    <FaTag /> {selectedImage.category}
                  </span>
                )}
                {/* {selectedImage.created_at && (
                  <span className="gallery-modal-date">
                    <FaCalendarAlt /> {new Date(selectedImage.created_at).toLocaleDateString()}
                  </span>
                )} */}
                {selectedImage.featured && (
                  <span className="gallery-modal-featured">
                    ★ {t('featured') || 'Featured'}
                  </span>
                )}
              </div>

              {/* ✅ MODAL ACTION BUTTONS: Download + Close */}
              <div className="gallery-modal-actions">
                <button
                  type="button"
                  className={`gallery-modal-download-btn ${downloadingId === selectedImage.id ? 'gallery-download-btn--loading' : ''}`}
                  onClick={(e) => handleDownload(e, selectedImage)}
                  disabled={downloadingId === selectedImage.id}
                >
                  {downloadingId === selectedImage.id ? (
                    <>
                      <span className="gallery-download-spinner" />
                      {t('downloading') || 'Downloading…'}
                    </>
                  ) : (
                    <>
                      <FaDownload /> {t('downloadImage') || 'Download Image'}
                    </>
                  )}
                </button>
                <button className="gallery-modal-close-btn" onClick={closeModal}>
                  {t('close') || 'Close'} <FaTimes />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;